import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { auth } from '../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';

// Load Stripe (fallback to a test key if missing for local development)
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_test_TYooMQauvdEDq54NiTphI7jx");

const SignupForm = () => {
    const stripe = useStripe();
    const elements = useElements();
    const navigate = useNavigate();

    const [step, setStep] = useState(1);
    const [planType, setPlanType] = useState('UNPAID_LOT'); // UNPAID_LOT or PAID_LOT
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        business_name: '',
        contact_name: '',
        address: '',
        phone_number: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleNext = (e) => {
        e.preventDefault();
        setError('');
        if (step === 2) {
            if (!formData.email || !formData.password || !formData.business_name || !formData.contact_name) {
                setError('Please fill in all required fields.');
                return;
            }
        }
        setStep(step + 1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        let paymentMethodId = null;

        if (planType === 'UNPAID_LOT') {
            if (!stripe || !elements) {
                setError("Stripe has not loaded yet. Please try again.");
                setLoading(false);
                return;
            }
            
            const cardElement = elements.getElement(CardElement);
            const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: {
                    name: formData.contact_name,
                    email: formData.email,
                    phone: formData.phone_number
                }
            });

            if (stripeError) {
                setError(stripeError.message);
                setLoading(false);
                return;
            }
            paymentMethodId = paymentMethod.id;
        }

        try {
            // 1. Create the user in Firebase Auth
            const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
            const firebaseUser = userCredential.user;
            const token = await firebaseUser.getIdToken();

            // 2. Send the rest of the business details to our backend
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const response = await fetch(`${apiUrl}/signup`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    email: formData.email,
                    business_name: formData.business_name,
                    contact_name: formData.contact_name,
                    address: formData.address,
                    phone_number: formData.phone_number,
                    plan_type: planType,
                    payment_method_id: paymentMethodId
                })
            });

            const data = await response.json();
            
            if (!response.ok) {
                // If backend fails, we should ideally delete the Firebase user, but for now we just show error
                throw new Error(data.detail || 'Signup failed');
            }

            // Success handling
            if (planType === 'PAID_LOT' && data.account_link_url) {
                // Redirect to Stripe Connect onboarding
                window.location.href = data.account_link_url;
            } else {
                // Done, go to login
                alert('Account created successfully! Please log in.');
                navigate('/login');
            }

        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const cardStyle = {
        style: {
            base: {
                color: 'var(--text)',
                fontFamily: '"Inter", sans-serif',
                fontSmoothing: 'antialiased',
                fontSize: '16px',
                '::placeholder': {
                    color: '#aab7c4'
                }
            },
            invalid: {
                color: 'var(--danger)',
                iconColor: 'var(--danger)'
            }
        }
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '20px' }}>
            <div className="glass-card" style={{ padding: '40px', width: '100%', maxWidth: '500px' }}>
                <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                    <img src="/logo-brand.png" alt="Logo" style={{ height: '60px', marginBottom: '10px' }} />
                    <h2 style={{ margin: 0 }}>Join Wagoing</h2>
                    <p style={{ color: 'var(--text-muted)', marginTop: '10px' }}>Step {step} of 3</p>
                </div>

                {error && <div style={{ color: 'var(--danger)', marginBottom: '15px', fontSize: '14px', textAlign: 'center', padding: '10px', backgroundColor: 'rgba(231, 76, 60, 0.1)', borderRadius: '4px' }}>{error}</div>}

                {step === 1 && (
                    <div>
                        <h3 style={{ marginBottom: '20px' }}>Choose your plan</h3>
                        
                        <div 
                            style={{ 
                                padding: '20px', 
                                border: planType === 'UNPAID_LOT' ? '2px solid var(--primary)' : '1px solid var(--border)', 
                                borderRadius: '8px',
                                marginBottom: '15px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                            onClick={() => setPlanType('UNPAID_LOT')}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <h4 style={{ margin: 0, fontSize: '18px' }}>Unpaid Lot Operator</h4>
                                <span style={{ fontWeight: 'bold', color: 'var(--primary)' }}>$59/mo</span>
                            </div>
                            <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>Perfect for private lots or non-charging lots. Includes a 30-day free trial.</p>
                        </div>

                        <div 
                            style={{ 
                                padding: '20px', 
                                border: planType === 'PAID_LOT' ? '2px solid var(--primary)' : '1px solid var(--border)', 
                                borderRadius: '8px',
                                marginBottom: '30px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                            onClick={() => setPlanType('PAID_LOT')}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <h4 style={{ margin: 0, fontSize: '18px' }}>Paid Lot Operator</h4>
                                <span style={{ fontWeight: 'bold', color: 'var(--primary)' }}>Free</span>
                            </div>
                            <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>You charge drivers, we take a 7.5% management fee. Payouts sent directly to you.</p>
                        </div>

                        <button onClick={() => setStep(2)} className="btn btn-primary" style={{ width: '100%', padding: '12px', fontSize: '16px' }}>
                            Continue
                        </button>
                    </div>
                )}

                {step === 2 && (
                    <form onSubmit={handleNext}>
                        <h3 style={{ marginBottom: '20px' }}>Business Details</h3>
                        
                        <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>Contact Name</label>
                                <input type="text" name="contact_name" value={formData.contact_name} onChange={handleChange} required />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>Phone Number</label>
                                <input type="tel" name="phone_number" value={formData.phone_number} onChange={handleChange} required />
                            </div>
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>Business/Organization Name</label>
                            <input type="text" name="business_name" value={formData.business_name} onChange={handleChange} required />
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>Address</label>
                            <input type="text" name="address" value={formData.address} onChange={handleChange} required />
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>Email</label>
                            <input type="email" name="email" value={formData.email} onChange={handleChange} required />
                        </div>

                        <div style={{ marginBottom: '30px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>Password</label>
                            <input type="password" name="password" value={formData.password} onChange={handleChange} required />
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button type="button" onClick={() => setStep(1)} className="btn btn-secondary" style={{ flex: 1, padding: '12px', fontSize: '16px' }}>
                                Back
                            </button>
                            <button type="submit" className="btn btn-primary" style={{ flex: 2, padding: '12px', fontSize: '16px' }}>
                                Continue
                            </button>
                        </div>
                    </form>
                )}

                {step === 3 && (
                    <form onSubmit={handleSubmit}>
                        {planType === 'UNPAID_LOT' ? (
                            <>
                                <h3 style={{ marginBottom: '20px' }}>Payment Details</h3>
                                <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '20px' }}>
                                    Your card will be kept on file. You won't be charged today. The $59/mo subscription starts after your 30-day free trial.
                                </p>
                                <div style={{ 
                                    padding: '15px', 
                                    border: '1px solid var(--border)', 
                                    borderRadius: '6px', 
                                    marginBottom: '30px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                                }}>
                                    <CardElement options={cardStyle} />
                                </div>
                            </>
                        ) : (
                            <>
                                <h3 style={{ marginBottom: '20px' }}>Setup Payouts</h3>
                                <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '30px' }}>
                                    Since you will be charging drivers, we need to securely connect your bank account to route your funds. You will be redirected to Stripe to verify your identity and set up payouts.
                                </p>
                            </>
                        )}

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button type="button" onClick={() => setStep(2)} className="btn btn-secondary" disabled={loading} style={{ flex: 1, padding: '12px', fontSize: '16px' }}>
                                Back
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={loading} style={{ flex: 2, padding: '12px', fontSize: '16px' }}>
                                {loading ? 'Processing...' : (planType === 'UNPAID_LOT' ? 'Start Free Trial' : 'Continue to Stripe')}
                            </button>
                        </div>
                    </form>
                )}
                
                <div style={{ marginTop: '30px', textAlign: 'center', fontSize: '14px' }}>
                    Already have an account? <span style={{ color: 'var(--primary)', cursor: 'pointer' }} onClick={() => navigate('/login')}>Log In</span>
                </div>
            </div>
        </div>
    );
};

const Signup = () => {
    return (
        <Elements stripe={stripePromise}>
            <SignupForm />
        </Elements>
    );
};

export default Signup;
